// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Click-to-define glossary. Any element with `data-term` becomes a dotted link;
 * clicking it shows a small tooltip. Escape or an outside click dismisses it.
 */

export const GLOSSARY: Record<string, string> = {
  'frame differencing':
    'The core trick: reduce each frame to a coarse grid of average brightness, then compare it to the previous frame. Cells that changed a lot are "motion". No object recognition is involved.',
  sensitivity:
    'How much a scene must change to count as motion. Higher sensitivity reacts to smaller or dimmer movements — but also to flickering light and camera noise.',
  cooldown:
    'How long movement must be absent before a recording stops. A short cooldown makes lots of short clips; a longer one keeps a single event together.',
  'detection zone':
    'An optional rectangle. When set, only movement inside it triggers a recording — handy to watch a doorway while ignoring a busy window or a TV.',
  'wake lock':
    'A browser feature that keeps the screen from sleeping while the camera is armed, so a device left monitoring keeps watching.',
  'media recorder':
    'The browser API that encodes a clip from the live camera into a video file, entirely on your device. Sentrywell only starts it when motion is detected.',
  webm: 'A common, open video container. Motion clips are saved as WebM (or MP4 on Safari) — self-contained files you can play or share anywhere.',
  indexeddb:
    'A private database inside your browser, on this device. Captured events live here until you delete them — they are never uploaded.',
};

let tip: HTMLElement | null = null;

function ensureTip(): HTMLElement {
  if (tip) return tip;
  tip = document.createElement('div');
  tip.className = 'glossary-tip';
  tip.hidden = true;
  document.body.appendChild(tip);
  return tip;
}

export function initGlossary(): void {
  document.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement;
    const link = target.closest('[data-term]') as HTMLElement | null;
    if (link) {
      ev.preventDefault();
      const term = (link.dataset.term ?? '').toLowerCase();
      const def = GLOSSARY[term];
      if (!def) return;
      const t = ensureTip();
      t.textContent = def;
      t.hidden = false;
      const rect = link.getBoundingClientRect();
      t.style.top = `${rect.bottom + window.scrollY + 6}px`;
      const left = Math.min(rect.left + window.scrollX, window.innerWidth - 320);
      t.style.left = `${Math.max(8, left)}px`;
      return;
    }
    if (tip && !tip.hidden && !target.closest('.glossary-tip')) tip.hidden = true;
  });
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && tip && !tip.hidden) tip.hidden = true;
  });
}
